/* eslint-disable no-param-reassign */
import { http } from '../index';
import { formDataModifier } from '../modifiers';
import { getTestSlug } from 'utils';

const multiPartConfig = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
};

export const getCompany = id => {
  return http
    .get(`/company/${id}`)
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const getCompanies = params => {
  return http
    .get('/companies', {
      params,
    })
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const createCompany = data => {
  const config = [];
  config.push(data);
  if (data.logo && typeof data.logo !== 'string' && data.logo.length > 0) {
    const formData = formDataModifier(data);
    config[0] = formData;
    config.push(multiPartConfig);
  }
  return http
    .post('/companies', config[0], config[1])
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const updateCompany = (data, companyId) => {
  const config = [];
  config.push(data);
  if (data.logo && typeof data.logo !== 'string' && data.logo.length > 0) {
    const formData = formDataModifier(data);
    config[0] = formData;
    config.push(multiPartConfig);
  }

  return http
    .put(`/company/${companyId}`, config[0], config[1])
    .then(res => res.data)
    .catch(error =>
      error && error.response && error.response.data
        ? error.response.data
        : {
            error: true,
            statusCode: 500,
            message: ['errors.serverError'],
          },
    );
};

export const deleteCompany = ({ id }) => {
  return http
    .delete(`/company/${id}`)
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const deleteCompanyStation = ({ stationId, companyId }) => {
  return http
    .delete(`/company/${companyId}/station/${stationId}`)
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const disableCompany = ({ id }) => {
  return http
    .get(`/company/${id}/disable`)
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const enableCompany = ({ id }) => {
  return http
    .get(`/company/${id}/enable`)
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const toggleCompanyState = (id, enable) => {
  return http
    .get(`/company/${id}/${enable}`)
    .then(res => res.data)
    .catch(error => error.response.data);
};

export const getCompanyJobRoles = companyId => {
  return http
    .get(`/company/${companyId}/job-roles`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

export const getCompanyJobRolesCatalog = companyId => {
  return http
    .get(`/company/${companyId}/job-roles/catalog`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

export const updateCompanyJobRoles = (companyId, data) => {
  return http
    .put(`/company/${companyId}/job-roles`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

export const getCompanyTrainingModules = companyId => {
  return http
    .get(`/company/${companyId}/training-modules`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

export const getCompanyTrainingModulesCatalog = companyId => {
  return http
    .get(`/company/${companyId}/training-modules/catalog`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

export const getCompanyTrainingModulesByJobRoles = (companyId, data) => {
  return http
    .post(`/company/${companyId}/training-modules/by-job-roles`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

export const updateCompanyTrainingModules = (companyId, data) => {
  return http
    .put(`/company/${companyId}/training-modules`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);
};

// Store companyId globally to use in all requests
let globalCompanyId = null;
let companyIdPromise = null;

// Set up interceptor once to always include companyId header if available
http.interceptors.request.use(config => {
  if (globalCompanyId) {
    config.headers.companyid = globalCompanyId;
  }
  return config;
});

export const getCompanyInfoBySlug = data => {
  return http
    .get('/companies/info', {
      params: {
        slug: data,
      },
    })
    .then(res => {
      // Set companyId globally so it's included in all subsequent requests
      if (res.data && res.data.id && !res.data.error) {
        globalCompanyId = res.data.id;
        http.defaults.headers.common.companyid = res.data.id;
      }
      return res.data;
    })
    .catch(error => {
      // Don't set globalCompanyId on error
      return error.response ? error.response.data : { error: true };
    });
};

// Initialize companyId if REACT_APP_TEST_SLUG is set
export const initializeCompanyId = () => {
  const testSlug = getTestSlug();

  if (testSlug && testSlug.trim() !== '' && !globalCompanyId && !companyIdPromise) {
    companyIdPromise = getCompanyInfoBySlug(testSlug.trim())
      .then(companyData => {
        // Only return companyId if data is valid and has no error
        if (companyData && companyData.id && !companyData.error) {
          return companyData.id;
        }
        return null;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        companyIdPromise = null;
      });
    return companyIdPromise;
  }

  return Promise.resolve(globalCompanyId);
};

// Get current companyId (wait for initialization if needed)
export const getCompanyId = () => {
  if (globalCompanyId) {
    return Promise.resolve(globalCompanyId);
  }

  if (companyIdPromise) {
    return companyIdPromise;
  }

  return initializeCompanyId();
};
